import React, {useEffect, useState} from "react";
import {connect} from "react-redux";
import {bindActionCreators} from "redux";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import UNINITIALISED from "utils/uninitialised";
import requestor from "requestor";
import clientContextsFetchAction from "modules/client_context/actions/client_contexts_fetch";
import clientContextCreateAction from "modules/client_context/actions/client_context_create";
import {ClientContext, ClientContextId} from "common/types/client_context";
import {OrganisationId} from "common/types/organisation";
import {
  ClientContextFields,
  ContextControl,
  ContextControlVariant,
  contextSummaryLabel,
  useClientContextEditor,
} from "common_components/client_context";

interface MatterContextButtonProps {
  organisationId: OrganisationId;
  context: string;
  clientContextId?: ClientContextId | null;
  isProjectContextDisabled: boolean;
  onSave: (
    context: string,
    clientContextId: ClientContextId | null,
    isProjectContextDisabled: boolean,
  ) => void;
  variant?: ContextControlVariant;
  clientContexts: ClientContext[];
  fetchClientContexts: (organisationId: OrganisationId) => void;
  createClientContext: (
    organisationId: OrganisationId,
    name: string,
    context: string,
  ) => Promise<{value: ClientContext}>;
}

const MatterContextButton: React.FC<MatterContextButtonProps> = ({
  organisationId,
  context,
  clientContextId,
  isProjectContextDisabled,
  onSave,
  variant,
  clientContexts,
  fetchClientContexts,
  createClientContext,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [useProjectContext, setUseProjectContext] = useState(true);
  const editor = useClientContextEditor(clientContexts, (name, text) =>
    createClientContext(organisationId, name, text).then(
      result => result.value,
    ),
  );

  useEffect(() => {
    fetchClientContexts(organisationId);
  }, [organisationId]);

  const handleOpen = () => {
    editor.reset(context, clientContextId ?? null);
    setUseProjectContext(!isProjectContextDisabled);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleRemove = () => {
    onSave("", null, isProjectContextDisabled);
    setIsOpen(false);
  };

  const handleSave = async () => {
    const committed = await editor.commit();
    if (!committed) {
      return;
    }
    onSave(editor.editedContext, committed.clientContextId, !useProjectContext);
    setIsOpen(false);
  };

  const hasToggleChanged = useProjectContext !== !isProjectContextDisabled;
  const hasChanged =
    editor.hasChangedFrom(context, clientContextId ?? null) || hasToggleChanged;
  const label = contextSummaryLabel(
    context,
    clientContextId ?? null,
    clientContexts,
  );
  const hasStoredContext = Boolean(label);

  return (
    <>
      <ContextControl label={label} variant={variant} onClick={handleOpen} />
      <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogContent style={{paddingTop: "16px"}}>
          <ClientContextFields
            editor={editor}
            textPlaceholder={
              "Add rules or context that should be taken into account " +
              "when analysing documents in this contract family"
            }
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                color="primary"
                checked={useProjectContext}
                onChange={e => setUseProjectContext(e.target.checked)}
              />
            }
            label="Include project context in analysis"
            style={{marginTop: "12px"}}
          />
        </DialogContent>
        <DialogActions style={{padding: "8px 24px 16px"}}>
          {hasStoredContext && (
            <Button
              size="small"
              onClick={handleRemove}
              style={{marginRight: "auto", color: "#c62828"}}
            >
              Remove context
            </Button>
          )}
          <Button size="small" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            size="small"
            color="primary"
            variant="contained"
            onClick={handleSave}
            disabled={!hasChanged || editor.isOverLimit}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const NO_CLIENT_CONTEXTS: ClientContext[] = [];

const mapStateToProps = (state: {
  clientContexts: ClientContext[] | typeof UNINITIALISED;
}) => ({
  clientContexts:
    state.clientContexts === UNINITIALISED
      ? NO_CLIENT_CONTEXTS
      : state.clientContexts,
});

function mapDispatchToProps(dispatch) {
  return {
    ...bindActionCreators(
      {
        fetchClientContexts: clientContextsFetchAction(requestor),
        createClientContext: clientContextCreateAction(requestor),
      },
      dispatch,
    ),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(MatterContextButton);
